import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router";
import { AnimatePresence, motion } from "framer-motion";
import { AlertCircle, ArrowLeft, Mail, RefreshCw } from "lucide-react";
import { useDispatch } from "react-redux";
import AuthLayout from "../../components/auth/AuthLayout";
import DemoOtpModal from "../../components/auth/DemoOtpModal";
import Button from "../../components/ui/Button";
import Input from "../../components/ui/Input";
import OtpInput from "../../components/ui/OtpInput";
import { loginSuccess } from "../../store/slices/authSlice";
import {
  requestLoginOtp,
  verifyLoginOtp,
  resendLoginOtp,
  USE_MOCK_AUTH,
} from "../../services/authApi";
import { getMockCredentialsHint } from "../../services/mockAuth";
import { showSuccess } from "../../utils/toast";

const RESEND_COOLDOWN = 60;

const LOGIN_STEPS = [{ label: "Email" }, { label: "Verify OTP" }];

const stepVariants = {
  enter: (direction) => ({ y: direction > 0 ? 16 : -16, opacity: 0 }),
  center: { y: 0, opacity: 1 },
  exit: (direction) => ({ y: direction > 0 ? -16 : 16, opacity: 0 }),
};

export default function LoginPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const hint = getMockCredentialsHint();

  const [step, setStep] = useState(1);
  const [direction, setDirection] = useState(1);
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);
  const [demoOtp, setDemoOtp] = useState("");
  const [otpModalOpen, setOtpModalOpen] = useState(false);

  useEffect(() => {
    if (resendCooldown <= 0) return undefined;
    const timer = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const goToStep = (next) => {
    setDirection(next > step ? 1 : -1);
    setStep(next);
    setError("");
  };

  const handleSendOtp = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const result = await requestLoginOtp(email);
      setOtp("");
      setResendCooldown(RESEND_COOLDOWN);
      setDemoOtp(result.demo_otp ?? "");
      setOtpModalOpen(Boolean(result.demo_otp));
      goToStep(2);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = useCallback(
    async (code) => {
      const value = code ?? otp;
      if (value.length < 6 || loading) return;

      setError("");
      setLoading(true);
      try {
        const result = await verifyLoginOtp(email, value);
        dispatch(loginSuccess(result));
        const firstName = result.user.name?.split(" ")[0] ?? "there";
        showSuccess(`Welcome back, ${firstName}!`);
        navigate("/dashboard", { replace: true });
      } catch (err) {
        setError(err.message);
        setOtp("");
      } finally {
        setLoading(false);
      }
    },
    [otp, loading, email, dispatch, navigate]
  );

  const handleOtpChange = (value) => {
    setOtp(value);
    setError("");
    if (value.length === 6) {
      handleVerifyOtp(value);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0 || loading) return;
    setError("");
    setLoading(true);
    try {
      const result = await resendLoginOtp(email);
      setOtp("");
      setResendCooldown(RESEND_COOLDOWN);
      setDemoOtp(result.demo_otp ?? "");
      setOtpModalOpen(Boolean(result.demo_otp));
      showSuccess("A new code has been sent to your email.");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      title={step === 1 ? "Institution sign-in" : "Enter verification code"}
      subtitle={
        step === 1
          ? "Use the primary email SimCollect registered for your institution (one login per client)."
          : `Code sent via email to ${email}`
      }
      badge="Institution portal"
      steps={LOGIN_STEPS}
      currentStep={step}
    >
      <AnimatePresence mode="wait" custom={direction}>
        {step === 1 && (
          <motion.form
            key="email"
            custom={direction}
            variants={stepVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.22 }}
            onSubmit={handleSendOtp}
            className="space-y-4"
          >
            {error && (
              <div className="flex items-start gap-3 border-l-2 border-red-400 bg-red-50/80 px-4 py-2.5">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-500" />
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            <Input
              label="Email address"
              type="email"
              placeholder="you@institution.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              icon={Mail}
              variant="underline"
              required
              autoComplete="email"
            />

            <Button type="submit" variant="accent" loading={loading} className="w-full" size="lg">
              Send verification code
            </Button>

            {USE_MOCK_AUTH ? (
              <div className="border border-primary/8 bg-primary/[0.03] p-3">
                <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-primary/40">
                  Demo access
                </p>
                <p className="mt-1 text-xs text-primary/65">
                  <span className="font-semibold">{hint.email}</span>
                </p>
                <p className="mt-1 text-[11px] text-primary/45">{hint.note}</p>
                <button
                  type="button"
                  onClick={() => setEmail(hint.email)}
                  className="mt-2 text-[10px] font-bold uppercase tracking-wider text-accent-dark transition-colors hover:text-primary"
                >
                  Use demo email
                </button>
              </div>
            ) : null}
          </motion.form>
        )}

        {step === 2 && (
          <motion.form
            key="otp"
            custom={direction}
            variants={stepVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.22 }}
            onSubmit={(e) => {
              e.preventDefault();
              handleVerifyOtp();
            }}
            className="space-y-4"
          >
            {error && (
              <div className="flex items-start gap-3 border-l-2 border-red-400 bg-red-50/80 px-4 py-2.5">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-500" />
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            <div>
              <p className="mb-3 text-[11px] font-medium uppercase tracking-[0.14em] text-primary/70">
                6-digit code
              </p>
              <OtpInput value={otp} onChange={handleOtpChange} disabled={loading} />
            </div>

            <Button
              type="submit"
              variant="accent"
              loading={loading}
              disabled={otp.length < 6}
              className="w-full"
              size="lg"
            >
              Verify & sign in
            </Button>

            {demoOtp ? (
              <button
                type="button"
                onClick={() => setOtpModalOpen(true)}
                className="w-full rounded-lg border border-collect/20 bg-collect/[0.04] px-3 py-2 text-center text-xs font-semibold text-collect transition-colors hover:bg-collect/[0.08]"
              >
                Show demo code again
              </button>
            ) : null}

            <div className="flex items-center justify-between gap-3 pt-1">
              <button
                type="button"
                onClick={() => goToStep(1)}
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary/50 transition-colors hover:text-primary"
              >
                <ArrowLeft className="h-3.5 w-3.5" />
                Change email
              </button>
              <button
                type="button"
                onClick={handleResend}
                disabled={resendCooldown > 0 || loading}
                className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-accent-dark transition-colors hover:text-primary disabled:cursor-not-allowed disabled:text-primary/30"
              >
                <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
                {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Resend code"}
              </button>
            </div>
          </motion.form>
        )}
      </AnimatePresence>

      <DemoOtpModal
        open={otpModalOpen}
        otp={demoOtp}
        channel="email"
        onClose={() => setOtpModalOpen(false)}
        onUseCode={(code) => handleOtpChange(code)}
      />
    </AuthLayout>
  );
}
